import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef, useState } from 'react';
import { usePostBlob } from '~/hooks';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { MAX_ATTACHMENT_FILES, PUBLISH_TYPE } from '../constants';
import { generateRequestBody } from '../logics';
import { CreateInternalNewsFormValues, createInternalNewsSchema } from '../logics/schema';
import { CreateInternalNewsPresentation } from '../presentations';
import { usePostInternalNews } from './usePostInternalNews';

type PresentationProps = React.ComponentProps<typeof CreateInternalNewsPresentation>;

type IUseCreateInternalNews = () => {
  state: {
    methods: UseFormReturn<CreateInternalNewsFormValues>;
    isSubmitDisabled: boolean;
    publishMode: PresentationProps['publishMode'];
    files: PresentationProps['attachedFiles'];
    isFileUploading: PresentationProps['isFileUploading'];
    isFileUploadDisabled: PresentationProps['isFileUploadDisabled'];
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
    handleFilesChange: PresentationProps['onFilesChange'];
  };
};

/**
 * 社内報作成フォームの状態管理とアクションを提供するコンポーネントフック。
 * 即時公開時は AlertDialog で確認後に POST、予約投稿時は直接 POST する。
 * ファイルアップロードの管理も担い、usePostBlob で都度アップロードして signed_id を管理する。
 */
export const useCreateInternalNews: IUseCreateInternalNews = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showAlertDialog } = useAlertDialog();
  const threeLetterCode = getThreeLetterCode();
  const [files, setFiles] = useState<File[]>([]);
  // File → signed_id のマッピング。parallel array の index ずれを防ぐため Map で管理する
  const fileSignedIdMapRef = useRef<Map<File, string>>(new Map());
  // アップロード中に削除されたファイルを追跡する Set。onSuccess でのレースコンディション防止用
  const cancelledFilesRef = useRef<Set<File>>(new Set());

  const methods = useForm<CreateInternalNewsFormValues>({
    defaultValues: {
      title: '',
      body: '',
      mainImage: undefined,
      publishMode: PUBLISH_TYPE.IMMEDIATELY,
      scheduledDate: undefined,
      scheduledTime: '',
      fileSignedIds: [],
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(createInternalNewsSchema),
  });

  const publishMode = methods.watch('publishMode');

  const { trigger, isMutating } = usePostInternalNews(threeLetterCode, {
    onSuccess: data => {
      showToast({
        message:
          publishMode === PUBLISH_TYPE.SCHEDULED
            ? '社内報を予約しました。'
            : '社内報を公開しました。',
        status: 'success',
      });
      navigate(replaceRouteParams(ROUTES.INTERNAL_NEWS.DETAIL, { id: data.client_newsletter.id }));
    },
    onError: () => {
      showToast({ message: '社内報の作成に失敗しました。', status: 'danger' });
    },
  });

  const { trigger: triggerBlob, isMutating: isFileMutating } = usePostBlob(threeLetterCode);

  /**
   * フォーム送信ハンドラ。即時公開の場合は AlertDialog で確認後に POST する。
   * 予約投稿の場合は確認なしで直接 POST する。
   * generateRequestBody（Base64 変換を含む）が失敗した場合はエラートーストを表示して早期 return する。
   */
  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(async data => {
    const executeSubmit = async () => {
      const requestBody = await generateRequestBody(data).catch(() => {
        showToast({
          message: 'メイン画像の読み込みに失敗したため、社内報を作成できませんでした。',
          status: 'danger',
        });
      });

      if (!requestBody) return;
      trigger(requestBody);
    };

    if (data.publishMode === PUBLISH_TYPE.IMMEDIATELY) {
      showAlertDialog({
        title: '今すぐ公開しますか？',
        message: '現在の時刻で社内報が投稿され、利用者にプッシュ通知での案内が届きます。',
        confirmText: '公開する',
        onConfirm: executeSubmit,
      });
    } else {
      await executeSubmit();
    }
  });

  /**
   * ファイル変更ハンドラ。
   * 追加されたファイルをアップロードして signed_id を RHF の fileSignedIds に追加し、
   * 削除されたファイルの signed_id を RHF の fileSignedIds から除去する。
   * 添付ファイル数が MAX_ATTACHMENT_FILES を超えた場合は早期リターンする。
   */
  const handleFilesChange = (newFiles: File[]) => {
    // 件数上限を超えた場合はファイルを追加しない
    if (newFiles.length > MAX_ATTACHMENT_FILES) return;

    // 削除されたファイルを Map から除去して RHF を更新する。
    // アップロード中のファイルは Map に未登録のため、cancelledFilesRef で追跡して
    // onSuccess でのレースコンディションを防ぐ
    for (const file of [...fileSignedIdMapRef.current.keys()]) {
      if (!newFiles.includes(file)) {
        fileSignedIdMapRef.current.delete(file);
      }
    }
    for (const file of files) {
      if (!newFiles.includes(file)) {
        cancelledFilesRef.current.add(file);
      }
    }
    methods.setValue('fileSignedIds', [...fileSignedIdMapRef.current.values()]);

    // 追加されたファイルをアップロードする。
    // 本来 onSuccess / onError は hook 呼び出し時に定義するが、以下の理由で trigger 時に定義する。
    // - onSuccess: 複数ファイルの並列アップロード時に完了順が不定となるため、
    //   どの file に対する signed_id かをクロージャで特定する必要がある
    // - onError: 失敗した file をクロージャで特定して files state から除去する必要がある
    for (const file of newFiles.filter(file => !files.includes(file))) {
      triggerBlob(
        { 'blob[file]': file },
        {
          onSuccess: data => {
            // 削除済みファイルの signed_id は追加しない（レースコンディション対策）
            if (cancelledFilesRef.current.has(file)) {
              cancelledFilesRef.current.delete(file);
              return;
            }
            fileSignedIdMapRef.current.set(file, data.blob.signed_id);
            methods.setValue('fileSignedIds', [...fileSignedIdMapRef.current.values()]);
          },
          onError: () => {
            cancelledFilesRef.current.delete(file);
            showToast({ message: 'ファイルのアップロードに失敗しました。', status: 'danger' });
            setFiles(prevFiles => prevFiles.filter(prevFile => prevFile !== file));
          },
        }
      );
    }

    setFiles(newFiles);
  };

  return {
    state: {
      methods,
      isSubmitDisabled: isMutating || isFileMutating,
      publishMode,
      files,
      isFileUploading: isFileMutating,
      isFileUploadDisabled: isFileMutating || files.length >= MAX_ATTACHMENT_FILES,
    },
    actions: {
      handleSubmit,
      handleFilesChange,
    },
  };
};
